// PE-22 K2.3 — the Draft-side twin of programPrescription's
// programWeeklyPrescription (AP-7), computed from the editor's
// in-construction Draft instead of a saved Program, so the composer's
// coverage panel (mockup §03: "Pettorali 18 · Polpacci 0") recolours live
// as the coach edits. Same shape, same math, same averaging rule (Antenore
// 2026-08-27: Σ hard-sets×credits ÷ cycleWeeks, the full-cycle average) — a
// coach must never see two different "what does this program train" numbers
// depending on whether they're mid-edit or looking at the saved Home preview.
//
// isPrescribedHardSetByEffort (programPrescription.ts) reads a resolved
// PlannedSet's targetRir/targetRpe. The Draft carries the same decision
// differently: one intensityKind per ROW (a cell may hold its own, kindAt)
// plus one value per WEEK cell. So isPrescribedHardSetDraft below maps that shape onto the
// SAME shared rule: an explicit 'rir' target is hard only at/below
// EFFORT_HARD_SET_RIR, an explicit 'rpe' target is hard only at/above its
// RPE-scale mirror (10 - EFFORT_HARD_SET_RIR), and every other case —
// percent-of-max / absolute / none / history, OR a rir/rpe row whose cell
// carries no parseable value this week — has no explicit effort target, so
// it counts as a working set: "assumed hard" (same wording as
// isPrescribedHardSetByEffort's own comment).

import {
  isCellAbsent,
  kindAt,
  parseFloatLoose,
  type Draft,
  type SetRowDraft,
} from "./programEdit.js";
import type { Exercise } from "./schemas/exercise.js";
import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import { isPrescribedHardSetByEffort } from "./programPrescription.js";
import { catalogBySlug, resolveExerciseMuscleCredits } from "./volumeByMuscle.js";
import { HARD_SET_MUSCLE_CONTRIB_MIN } from "./weeklyTargetActual.js";

const isPrescribedHardSetDraft = (row: SetRowDraft, week: number): boolean => {
  const v = parseFloatLoose(row.cells[week]?.intensityValue ?? "");
  const kind = kindAt(row, week);
  return isPrescribedHardSetByEffort(
    kind === "rir" ? v : null,
    kind === "rpe" ? v : null,
  );
};

// Hard-set coverage per muscle, walking the draft's own sessions/slots (no
// macrocycle resolution the way programWeeklyPrescription walks
// resolveFullCycleSessions — the composer's draft-in-construction already
// enumerates every session once; alternating/phase-offset variants are a
// resolved-Program concern this editor doesn't express yet). Skips a week a
// slot opted out of (inactiveWeeks[w]) — the same gate
// buildIntensityGridFromDraft applies — and any slot whose exerciseId the
// catalog can't resolve (custom/untagged rows contribute nothing, same as
// the volume math). Weighted by the same piecewise hard-set credit the
// actuals use (resolveExerciseMuscleCredits, AP-6): a substantial mover
// earns a full set, a synergist a fractional one.
export const hardSetsByMuscleFromDraft = (
  draft: Draft,
  catalog: ReadonlyArray<Exercise>,
): Partial<Record<CatalogMuscleSlug, number>> => {
  const cycleWeeks = draft.cycleWeeks;
  if (!Number.isInteger(cycleWeeks) || cycleWeeks < 1) return {};
  const bySlug = catalogBySlug(catalog);
  const out: Partial<Record<CatalogMuscleSlug, number>> = {};
  for (const session of draft.sessions) {
    for (const slot of session.slots) {
      const ex = bySlug.get(slot.exerciseId);
      if (!ex) continue;
      const credits = resolveExerciseMuscleCredits(ex, HARD_SET_MUSCLE_CONTRIB_MIN);
      if (credits.length === 0) continue;
      for (let w = 0; w < cycleWeeks; w += 1) {
        if (slot.inactiveWeeks[w]) continue;
        const hardSets = slot.setRows.filter(
          (row) => !isCellAbsent(row, w) && isPrescribedHardSetDraft(row, w),
        ).length;
        if (hardSets === 0) continue;
        for (const [m, credit] of credits) {
          out[m] = (out[m] ?? 0) + hardSets * credit;
        }
      }
    }
  }
  for (const m of Object.keys(out) as CatalogMuscleSlug[]) {
    out[m] = (out[m] ?? 0) / cycleWeeks;
  }
  return out;
};
